import React, { useEffect, useCallback} from 'react';
import { useOutletContext } from 'react-router-dom';

import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { useSamples } from '../services/api/KeynomeAPIHooks';
import { useCanUpload } from './utils/uploadHooks';
import { useCreateSample, useCreateConcatenatedFastqgz } from '../services/api/KeynomeAPIMutations';
import { filterSupportedSampleTypes } from './utils/sampleTypes';

const SampleTypeSelect = ({ sampleType, setSampleType, isIllumina, canChangeType }) => {
  const { supportedSampleTypes: supportedSampleTypesData } = useOutletContext();
  const filteredSampleTypes = filterSupportedSampleTypes(supportedSampleTypesData, isIllumina);

  const handleChange = (event) => {
      setSampleType(event.target.value);
    };
  
    return (
      <Box> 
        <FormControl fullWidth sx={{ padding: '10px'}}>
          <Select
            value={sampleType}
            onChange={handleChange}
            sx={{ height: '35px'}}
            disabled={!canChangeType}
          >
            {filteredSampleTypes.map((sampleType) => (
              <MenuItem key={sampleType} value={sampleType}>{sampleType}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
}

function AddSamplesFileListItem(props) {
    const {
        activeRun,
        isIllumina,
        stagedFile,
        removeStagedFile,
        setStagedFileAttribute,
        uploadFile,
    } = props;

    const { setUserErrorMessage } = useOutletContext();
    const { sampleName, R1: file, R2: r2File, sampleType, status: uploadingState } = stagedFile;

    const { data: samples, refetch: refetchSamples } = useSamples(activeRun?.id);
    const { canUpload, isLoading } = useCanUpload(samples, sampleName);

    const { mutateAsync: createSample } = useCreateSample();
    const { mutateAsync: createConcatenatedFastqgz } = useCreateConcatenatedFastqgz();

    useEffect(() => {
      setStagedFileAttribute(activeRun.id, sampleName, "canUpload", canUpload);
    }, [activeRun, sampleName, canUpload, setStagedFileAttribute])

    const runCreateSample = useCallback(() => {
      setStagedFileAttribute(activeRun.id, sampleName, 'status', 'creating_lib_seq');
      return createSample(
        { runId: activeRun.id, sampleType, sampleName, file },
        {
          onError: (error) => {
            if (error.message.includes('422')) {
              setUserErrorMessage('Failed to create sample record in Keynome Cloud! Please confirm that the sample name is less than 100 characters and try again. Contact support@dayzerodiagnostics.com if you require assistance.');
            } else {
              setUserErrorMessage('Failed to create sample record in Keynome Cloud! Please wait a few minutes and try again. Contact support@dayzerodiagnostics.com if you require assistance.');
            }
            throw error;
          } 
        }
      )
    }, [createSample, activeRun, file, sampleName, sampleType, setUserErrorMessage, setStagedFileAttribute]);

    const runCreateConcatenatedFastqgz = useCallback((newSample) => {
      setStagedFileAttribute(activeRun.id, sampleName, 'status', 'creating_concatenated_fastqgz');

      let extraFields = ["location_signed_upload_url"];
      if (isIllumina) extraFields.push("location_r2_signed_upload_url");

      return createConcatenatedFastqgz(
        { libSeqId: newSample.id, queryParams: { extraFields } },
        {
          onError: (error) => {
            setUserErrorMessage('Failed to create FASTQ record in Keynome Cloud! Please wait a few minutes and try again. Contact support@dayzerodiagnostics.com if you require assistance.');
            throw error;
          }
        }
      );
    }, [createConcatenatedFastqgz, isIllumina, activeRun, sampleName, setUserErrorMessage, setStagedFileAttribute]);

    const runUploadFile = useCallback((newConcatenatedFastqgz) => {
      setStagedFileAttribute(activeRun.id, sampleName, 'status', 'uploading_file');
      return uploadFile(newConcatenatedFastqgz, file, r2File);
    }, [uploadFile, activeRun, sampleName, file, r2File, setStagedFileAttribute]);

    const uploadSample = useCallback(() => {
        runCreateSample()
          .then((newSample) => runCreateConcatenatedFastqgz(newSample))
          .then((newConcatenatedFastqgz) => runUploadFile(newConcatenatedFastqgz))
          .then(() => {
            setStagedFileAttribute(activeRun.id, sampleName, 'status', 'success');
            refetchSamples();
          })
          .catch((error) => {
            setStagedFileAttribute(activeRun.id, sampleName, 'status', 'failed');
            console.error(error);
          })
    }, [runCreateSample, runCreateConcatenatedFastqgz, runUploadFile, setStagedFileAttribute, refetchSamples, activeRun, sampleName]);

    const totalFileSize = isIllumina ? ((file?.size | 0) + (r2File?.size | 0)) : file.size
    const i = totalFileSize === 0 ? 0 : Math.floor(Math.log(totalFileSize) / Math.log(1024));
    const humanReadableFileSize = ((totalFileSize / Math.pow(1024, i)).toFixed(2)) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];

    let icons = null;
    if (!uploadingState) {
        icons = (
            <>
            {((!isIllumina || (file !== null && r2File !== null))) ? (
              (!canUpload && !isLoading) ? (<small style={{marginTop: "10px", marginRight: "10px", color: "grey"}}><b>(Already exists - rename to upload)</b></small>) : (
                <Button sx={{marginTop: "5px", marginRight: "10px"}} variant="contained" color="primary" aria-label="upload" disabled={isLoading} onClick={() => uploadSample()} startIcon={<UploadIcon />}>Upload</Button> )) : null}
            <Button sx={{marginLeft: "5px", marginRight: "10px", marginTop: "5px"}} variant="outlined" color="error" aria-label="cancel" onClick={() => removeStagedFile(activeRun.id, sampleName)} endIcon={<DeleteIcon />}>CANCEL</Button>
            </>
        )
    } else if (uploadingState === 'success') {
        icons = (
            <CheckCircleOutlineIcon fontSize="large" color="success" sx={{marginTop: "7px", marginRight: "30px"}} />
        )
    } else if (uploadingState === 'failed') {
        icons = (
            <ErrorOutlineIcon fontSize="large" color="warning" sx={{marginTop: "7px", marginRight: "30px"}} />
        )
    } else {
        const steps = {
            creating_lib_seq: 1,
            creating_concatenated_fastqgz: 2,
            uploading_file: 3
        }

        let progress = (steps[uploadingState] / 5) * 100
        icons = (
                <Box sx={{paddingTop: "10px"}}>
              <CircularProgress
                    sx={{marginRight: "35px"}}
                    size={27}
                    value={uploadingState === 'uploading_file' ? "" : progress}
                    variant={uploadingState === 'uploading_file'? "indeterminate" : "determinate"}
                  />
            </Box>
        )
    }

    return (
      <Box>
        <Grid
          container
          sx={{
              pl: 2, pr: 1, textAlign: 'center', borderBottom: '#bdbdbd 2px solid', fontSize: 15
          }}
        >
          <Grid item xs={12} sm={2} sx={{ textAlign: 'left' }}>
            <p style={{
                'overflow': 'hidden',
                'whiteSpace': 'nowrap',
                'textOverflow': 'ellipsis'
            }}>
              <b>
                {sampleName}
              </b>
            </p>
          </Grid>
          <Grid item xs={1.5} sx={{ textAlign: 'center' }}>
            {canUpload ?
              <SampleTypeSelect
                sampleType={sampleType}
                setSampleType={(type) => setStagedFileAttribute(activeRun.id, sampleName, 'sampleType', type)}
                isIllumina={isIllumina}
                canChangeType={uploadingState === null}
              />
              : null
            }
          </Grid>
          <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
            <p>{(file && file.path) ? file.path.replace('./', '') : '-'}</p>
          </Grid>
        {
            isIllumina ? 
            (
              <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
                <p>{(r2File && r2File.path) ? r2File.path.replace('./', '') : '-'}</p>
              </Grid>
            ) : null

        }
          <Grid item xs={12} sm={1.5} sx={{ textAlign: 'center' }}>
            <p>{ humanReadableFileSize }</p>
          </Grid>
        {
            isIllumina ? null : (
                  <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
                    <p>{file.lastModifiedDate ? file.lastModifiedDate.toLocaleString() : '-'}</p>
                  </Grid>
            )
        }
          <Grid item xs={12} sm={3} sx={{ textAlign: 'right' }}>
            {icons}
          </Grid>
        </Grid>
      </Box>
    )
}

export default AddSamplesFileListItem;
